import React from 'react';
import Card from 'components/atoms/Card/Card';
import Heading from 'components/atoms/Heading/Heading';
import Txt from 'components/atoms/Txt/Txt';
import A from 'components/atoms/A/A';
import classes from './MerchantCard.module.scss';
import clsx from 'clsx';
import Tag from 'components/atoms/Tag/Tag';
import tagConfigs from 'components/atoms/Tag/tagConfigs';
import { mobileConfigFromTags, OfferType } from 'utils/offers';
import useDeviceType from 'hooks/useDeviceType';

export type Props = {
  merchantName: string;
  title: string;
  bannerImageSrc?: string | null;
  offerUrl: string;
  description?: string;
  isHighlight?: boolean;
  className?: string;
  defaultImg?: string;
  tags?: string[];
  offerType?: OfferType;
};

const MerchantCard = ({
  merchantName,
  title,
  bannerImageSrc,
  offerUrl,
  description,
  isHighlight = false,
  className,
  defaultImg,
  tags = [],
  offerType = 'cashback',
}: Props) => {
  const finalBannerImageSrc = bannerImageSrc || defaultImg || '/coin.svg';
  const isDefaultIcon = !bannerImageSrc;
  const mobileConfig = mobileConfigFromTags(tags);
  const mobileText = mobileConfig?.text.replace('only', '');
  const device = useDeviceType();
  const showMobileBand = mobileConfig != null && device != null && !device[mobileConfig.deviceKey];
  const visibleTags = (tags ?? []).filter((tag) => !!tagConfigs[offerType]?.[tag]);

  const content = (
    <div className={clsx(classes.cardWrapper, isHighlight && classes.cardWrapperHighlight)}>
      {showMobileBand && (
        <div className={classes.band}>
          <Txt>{mobileText}</Txt>
        </div>
      )}
      <div
        className={clsx(
          classes.banner,
          isDefaultIcon && classes.bannerDefaultIcon,
          isHighlight && classes.bannerHighlight,
        )}
        style={{ '--banner-image': `url(${finalBannerImageSrc})` } as React.CSSProperties}
        data-testid="merchant-card-banner"
      ></div>
      <div className={classes.content}>
        {visibleTags.length > 0 && (
          <div className={classes.tags}>
            {visibleTags.map((tag) => (
              <Tag className={classes.tag} tag={tag} type={offerType} key={tag} />
            ))}
          </div>
        )}
        {isHighlight ? (
          <>
            {merchantName && (
              <Txt className={clsx(classes.provider, classes.providerHighlight)} skipTranslation>
                {merchantName}
              </Txt>
            )}
            {title && (
              <Heading
                level={2}
                className={clsx(classes.title, classes.titleHighlight)}
                skipTranslation
              >
                {title}
              </Heading>
            )}
            {description && (
              <Txt
                className={clsx(classes.description, classes.descriptionHighlight)}
                skipTranslation
              >
                {description}
              </Txt>
            )}
          </>
        ) : (
          <>
            {title && (
              <Heading level={2} className={classes.title} skipTranslation>
                {title}
              </Heading>
            )}
            {description && (
              <Txt className={classes.description} skipTranslation>
                {description}
              </Txt>
            )}
            {merchantName && (
              <Txt className={classes.provider} skipTranslation>
                {merchantName}
              </Txt>
            )}
          </>
        )}
      </div>
    </div>
  );

  return (
    <Card
      isLink
      className={clsx(classes.root, isHighlight && classes.rootHighlight, className)}
      data-testid="merchant-card"
    >
      <A href={offerUrl} className={classes.link}>
        {content}
      </A>
    </Card>
  );
};

export default React.memo(MerchantCard);
